/* Copyright (c) 2022-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { EMPTY, fromEvent, merge, NEVER, Observable } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import { TizenKeyCodes } from 'utils/lib/platform/tizen';
import { Logger, LoggingService } from '../logging/logging.service';
import { DeviceOSType } from '../platform-detector/platform-detector';
import { PlatformDetectorService } from '../platform-detector/platform-detector.service';
import { SystemInfoService } from '../system-info-service/system-info.service';
import { WindowService } from '../window/window.service';

interface KeyboardState {
    visible: boolean;
    height?: number;
}

interface WebOSKeyboardStateEventDetail {
    visibility: boolean;
}
type WebOSKeyboardStateEvent = CustomEvent<WebOSKeyboardStateEventDetail>;

function isTypeableElement(target: EventTarget | Element): target is HTMLInputElement | HTMLTextAreaElement {
    return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
}

function keyboardStateComparator(x: KeyboardState, y: KeyboardState): boolean {
    return x.visible === y.visible && x.height === y.height;
}

@Injectable({
    providedIn: 'root'
})
export class SystemVirtualKeyboardService {
    private logger: Logger;
    private window: Window;
    private _keyboardState$: Observable<KeyboardState>;

    private readonly hiddenTextId = 'hiddentext';
    private get visualViewport(): VisualViewport {
        return this.window.visualViewport;
    }

    private get document(): Document {
        return this.window.document;
    }

    /**
     * Observes the state of virtual keyboard having height occupied(in pixel) as one of the properties.
     *
     * @note This is only supported on Safari PWA and Chrome on Android.
     */
    get keyboardState$(): Observable<KeyboardState> {
        return this._keyboardState$.pipe(filter(state => state.height !== undefined));
    }

    /**
     * Observes the presence of the virtual keyboard if supported.
     *
     * @note If the device does not support virtual keyboard detection in
     * `createKeyboardState`, will return `EMPTY` to complete the subscription.
     */
    get keyboardVisible$(): Observable<boolean> {
        return this._keyboardState$.pipe(map(state => state.visible));
    }

    constructor(
        loggingService: LoggingService,
        windowService: WindowService,
        private systemInfoService: SystemInfoService,
        private ngZone: NgZone
    ) {
        this.logger = loggingService.getLogger('SystemVirtualKeyboardService');
        this.window = windowService.window;
        this._keyboardState$ = this.createKeyboardState().pipe(
            distinctUntilChanged(keyboardStateComparator),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private createKeyboardState(): Observable<KeyboardState> {
        return this.systemInfoService.getDeviceOSType().pipe(
            take(1),
            switchMap(osType => {
                switch (osType) {
                    case DeviceOSType.WebOS:
                        return this.createWebOSKeyboardState();
                    case DeviceOSType.Tizen:
                        return this.createTizenKeyboardState();
                    /* Platform-check-disable-next-line */
                    // Allow virtual keyboard on windows
                    case DeviceOSType.Windows:
                    case DeviceOSType.Android:
                        return this.createWindowsOrAndroidKeyboardState();
                    case DeviceOSType.IOS:
                    case DeviceOSType.IPadOS:
                        return this.createIOSKeyboardState();
                    default:
                        this.logger.warn('Attempting to monitor virtual keyboard state on unsupported platform');
                        return EMPTY;
                }
            })
        );
    }

    private createWebOSKeyboardState(): Observable<KeyboardState> {
        // keyboardStateChange is only supported in PWA on LG TV
        if (!PlatformDetectorService.isPWAModeOnLGTV()) {
            this.logger.warn('Virtual keyboard detection is only possible in PWA mode');
            return NEVER;
        }

        return this.ngZone.runOutsideAngular(() =>
            fromEvent<WebOSKeyboardStateEvent>(this.document, 'keyboardStateChange').pipe(
                map(
                    event =>
                        ({
                            visible: event.detail.visibility
                        }) as KeyboardState
                ),
                startWith({ visible: false })
            )
        );
    }

    private createTizenKeyboardState(): Observable<KeyboardState> {
        return this.ngZone.runOutsideAngular(() => {
            const isFocusInTypeableElement$ = merge(
                fromEvent(this.window, 'focusin').pipe(map(event => isTypeableElement(event.target))),
                fromEvent(this.window, 'focusout').pipe(mapTo(false))
            );

            /*
                Tizen virtual keyboard can be closed while the focus is still on typeable element:
                    1. by clicking Done in the virtual keyboard
                    2. by clicking Return (or Back) button on TV remote
                    3. auto dismissed after a certain amount of time
                    4. navigating out of the keyboard
                When this happens,`Cancel` or `65376` (Done) keyboard event would be dispatched,
                and we manually blur() the typeable element to ensure that "virtual keyboard is up iif a typeable element is focused".
                See https://developer.samsung.com/smarttv/develop/guides/user-interaction/keyboardime.html
            */
            const manuallyBlur$ = fromEvent<KeyboardEvent>(this.window, 'keydown').pipe(
                filter(
                    event =>
                        (event.keyCode === TizenKeyCodes.Done || event.key === 'Cancel') &&
                        isTypeableElement(event.target)
                ),
                tap(event => (event.target as HTMLElement).blur()),
                // Maps to never because we are only interested in the side-effects
                switchMap(_ => NEVER)
            );

            return merge(isFocusInTypeableElement$, manuallyBlur$).pipe(
                map(
                    isFocusInTypeableElement =>
                        ({
                            visible: isFocusInTypeableElement
                        }) as KeyboardState
                ),
                // If keyboard state is created after the focus has been fired, will miss the emission for keyboard is visible.
                // Need to involve checking whether current activeElement is typeable to deduce the state.
                startWith({ visible: isTypeableElement(this.document.activeElement) })
            );
        });
    }

    private createWindowsOrAndroidKeyboardState(): Observable<KeyboardState> {
        if (!this.visualViewport) {
            this.logger.warn('visualViewport is required for virtual keyboard detection');
            return NEVER;
        }

        return this.ngZone.runOutsideAngular(() => {
            const estimatedKeyboardHeight$ = fromEvent(this.visualViewport, 'resize').pipe(
                debounceTime(10),
                map(() => Math.floor(this.window.innerHeight - this.visualViewport.height)),
                distinctUntilChanged()
            );

            return estimatedKeyboardHeight$.pipe(
                map(estimatedKeyboardHeight => {
                    const isFocusInTypeableElement = isTypeableElement(this.document.activeElement);
                    const keyboardHeight = isFocusInTypeableElement ? estimatedKeyboardHeight : 0;
                    return {
                        visible: keyboardHeight > 0,
                        height: keyboardHeight
                    } as KeyboardState;
                }),
                tap(keyboardState => {
                    // If softkeyboard is closed and `hidden inputText` element is in focus, then blur the focus.
                    if (keyboardState?.height <= 0 && this.document.activeElement?.id === this.hiddenTextId) {
                        (this.document.activeElement as HTMLElement).blur();
                    }
                }),
                startWith({ visible: false, height: 0 })
            );
        });
    }

    private createIOSKeyboardState(): Observable<KeyboardState> {
        if (!this.visualViewport) {
            this.logger.warn('visualViewport is required for virtual keyboard detection');
            return NEVER;
        }

        return this.ngZone.runOutsideAngular(() => {
            // Detects the presence of virtual keyboard by guessing its height
            const estimatedKeyboardHeight$ = merge(
                fromEvent(this.visualViewport, 'resize'),
                fromEvent(this.window, 'orientationchange')
            ).pipe(
                // Edge case on real iPad while soft keyboard is up in Landscape:
                // After clicked the Hide Key on soft keyboard and rotate to Portrait,
                // The VisualViewPort.height in Portrait will still equal to the VisualViewPort.height in Landscape.
                // e.g. VisualViewPort in Landscape while no soft keyboard = 1366 x 1024 -> Portrait = 1024 x 1024 is incorrect.
                filter(() => this.visualViewport.height !== this.window.innerWidth),
                map(() => this.window.innerHeight - this.visualViewport.height),
                distinctUntilChanged()
            );

            return estimatedKeyboardHeight$.pipe(
                map(estimatedKeyboardHeight => {
                    const isFocusInTypeableElement = isTypeableElement(this.document.activeElement);
                    const keyboardHeight = isFocusInTypeableElement ? estimatedKeyboardHeight : 0;
                    return {
                        visible: keyboardHeight > 0,
                        height: keyboardHeight
                    } as KeyboardState;
                }),
                startWith({ visible: false, height: 0 })
            );
        });
    }
}
